// @ts-nocheck
// O Estudo do clube, com o layout do Caderno pessoal.
//
// O Estudo antigo era uma pilha de cartões cronológica com um card grande de
// marco, um carrossel de marcos e uma faixa de temas em cima. O Caderno pessoal
// (`book-study.tsx`) resolveu o mesmo problema muito melhor: capa como clima,
// uma espinha vertical ancorada na PÁGINA DO LIVRO, e uma barra de captura
// sempre à mão com texto, voz e foto.
//
// Ordenar pela página do livro, num clube, não é só estética:
//   · junta a página 40 de três pessoas diferentes, que é onde a conversa está;
//   · e esconde naturalmente o que está adiante de onde você parou.
//
// O que este arquivo NÃO faz: marcos, quiz, caixa de perguntas, insight final.
// Esses tipos continuam existindo no banco e continuam sendo renderizados quando
// aparecem no fluxo — só não são mais criáveis por aqui.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../services/apiV2';
import { AudioPlayer, useVoiceRecorder, fmtTime } from '../audio';
import { CameraCapture } from '../camera-capture';
import { MentionText } from '../mentions';
import { MentionInput } from '../mention-input';
import { aoVoltarFechar } from '../platform';
import { ImgCapa } from '../common';
import { StudyPost } from './estudo';

const MONO = 'Geist Mono, monospace';
const SERIF = 'Instrument Serif, serif';

// Os três tipos que sobrevivem, com a cor que já era a deles no Caderno.
const TIPOS = [
  { key: 'reflection', label: 'Reflexão', cor: 'var(--ink-3)', place: 'O que essa leitura mexeu em você?' },
  { key: 'citation', label: 'Citação', cor: 'var(--ember)', place: 'Digite o trecho que você grifou…' },
  { key: 'question', label: 'Pergunta', cor: 'var(--plum)', place: 'O que você quer perguntar ao grupo?' },
];
const CORDOTIPO = { reflection: 'var(--ink-3)', citation: 'var(--ember)', question: 'var(--plum)' };
const ROTULO = { reflection: 'REFLEXÃO', citation: 'CITAÇÃO', question: 'PERGUNTA', quiz: 'QUIZ', 'question-box': 'CAIXA', 'leader-answer': 'RESPOSTA DO LÍDER', 'insight-final': 'INSIGHT' };

const FILTROS = [
  { key: 'all', label: 'Tudo' },
  { key: 'citacoes', label: 'Citações' },
  { key: 'questions', label: 'Perguntas' },
  { key: 'fotos', label: 'Fotos' },
  { key: 'audios', label: 'Áudios' },
  { key: 'saved', label: 'Salvos' },
];
const MODOS = [{ key: 'texto', label: 'Texto' }, { key: 'voz', label: 'Voz' }, { key: 'foto', label: 'Foto' }];
const PAGINA = 24;

/* ── Peças pequenas ─────────────────────────────────────────────────────── */

const Ico = ({ d, size = 18, w = 1.6, fill = 'none' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={fill === 'none' ? 'currentColor' : 'none'} strokeWidth={w} strokeLinecap="round" strokeLinejoin="round">{d}</svg>
);
const icMic = <><rect x="9" y="2.5" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><line x1="12" y1="18" x2="12" y2="21.5" /><line x1="8.5" y1="21.5" x2="15.5" y2="21.5" /></>;
const icFoto = <><path d="M3 8.5a2 2 0 0 1 2-2h2l1.2-1.8h5.6L17 6.5h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><circle cx="12" cy="13" r="3.3" /></>;
const icMais = <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>;
const icVoltar = <polyline points="15 18 9 12 15 6" />;
const icLupa = <><circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" /></>;
const icBalao = <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.6-.7L3 20.5l1.4-4.9A8.3 8.3 0 0 1 3.6 11.5a8.4 8.4 0 0 1 9-8.4 8.4 8.4 0 0 1 8.4 8.4z" />;
const icMarcar = <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />;
const icFechar = <><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></>;
const icMais3 = <><circle cx="12" cy="5" r="1.4" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" /><circle cx="12" cy="19" r="1.4" fill="currentColor" stroke="none" /></>;

// Coração: o único "like" da tela. Por baixo ele é a reação `tocou`, que já
// existia — assim nenhum toque que alguém já deu vira zero.
const Coracao = ({ cheio, size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={cheio ? 'var(--ember)' : 'none'} stroke={cheio ? 'var(--ember)' : 'currentColor'} strokeWidth={1.7} strokeLinejoin="round">
    <path d="M12 20.5s-7.5-4.7-7.5-9.7A4.3 4.3 0 0 1 12 7.7a4.3 4.3 0 0 1 7.5 3.1c0 5-7.5 9.7-7.5 9.7z" />
  </svg>
);

function Avatar({ nome, url, size = 30, onClick }) {
  const letra = (String(nome || '?')[0] || '?').toUpperCase();
  let h = 0; for (let i = 0; i < String(nome || '').length; i++) h = (h * 31 + String(nome).charCodeAt(i)) % 360;
  const base = {
    width: size, height: size, flex: 'none', borderRadius: '50%', overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: 'none', padding: 0, cursor: onClick ? 'pointer' : 'default',
  };
  if (url) return <button onClick={onClick} style={{ ...base, background: 'var(--paper-3)' }}><img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" /></button>;
  return (
    <button onClick={onClick} style={{ ...base, background: `linear-gradient(150deg, hsl(${h} 42% 42%), hsl(${(h + 40) % 360} 38% 26%))`, color: '#F6F0E6', fontFamily: SERIF, fontSize: size * 0.46 }}>
      {letra}
    </button>
  );
}

/* ── O cartão de uma publicação ─────────────────────────────────────────── */
// Memoizado: numa espinha com centenas de itens, um like em um deles não pode
// re-renderizar os outros.
const Publicacao = React.memo(function Publicacao({ p, rotulo, meuId, podeGerenciar, onCurtir, onComentar, onMenu, onPerfil, comentariosAbertos, blocoComentarios }) {
  const cor = CORDOTIPO[p.kind] || 'var(--ink-3)';
  const curtiu = (p.yourReactions || []).includes('tocou');
  const curtidas = (p.reactions && p.reactions.tocou) || 0;
  const citacao = p.kind === 'citation';

  return (
    <div style={{ paddingBottom: 22 }}>
      {/* autor */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9 }}>
        <Avatar nome={p.author?.name} url={p.author?.avatarUrl} size={28} onClick={() => onPerfil && onPerfil(p.author?.id)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: 13.5, color: 'var(--ink-2)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.author?.name}</span>
            {p.author?.role === 'Admin' && <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '0.1em', color: 'var(--ember)' }}>LÍDER</span>}
          </div>
          <div style={{ fontFamily: MONO, fontSize: 9.5, color: 'var(--ink-4)', marginTop: 1 }}>
            {rotulo} · {p.timeAgo}
          </div>
        </div>
        <button onClick={() => onMenu(p)} aria-label="Mais" style={{ width: 28, height: 28, flex: 'none', border: 'none', background: 'transparent', color: 'var(--ink-4)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Ico d={icMais3} size={15} />
        </button>
      </div>

      {/* foto — a polaroid do Caderno: a foto É a citação */}
      {p.photoUrl && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
          <div style={{ background: '#f4efe4', padding: '8px 8px 13px', borderRadius: 3, boxShadow: '0 12px 28px rgba(0,0,0,.35)', transform: 'rotate(-1.6deg)', maxWidth: 232 }}>
            <img src={p.photoUrl} alt="" loading="lazy" style={{ width: '100%', borderRadius: 1, display: 'block' }} />
          </div>
        </div>
      )}

      {/* áudio */}
      {p.audioUrl && (
        <div style={{ marginBottom: p.text ? 11 : 4 }}>
          <AudioPlayer url={p.audioUrl} dur={p.audioDur || 1} wave={p.wave} compact />
        </div>
      )}

      {/* texto */}
      {p.text ? (
        citacao ? (
          <div style={{ fontFamily: SERIF, fontSize: 21, lineHeight: 1.36, color: 'var(--ink)', background: 'var(--ember-soft)', boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone', padding: '2px 6px', display: 'inline' }}>
            <MentionText text={p.text} />
          </div>
        ) : (
          <div style={{
            fontSize: p.kind === 'question' ? 18 : 15.5, lineHeight: 1.52, color: 'var(--ink)',
            fontFamily: p.kind === 'question' ? SERIF : 'inherit',
            borderLeft: p.kind === 'question' ? `3px solid ${cor}` : 'none',
            paddingLeft: p.kind === 'question' ? 13 : 0,
          }}>
            <MentionText text={p.text} />
          </div>
        )
      ) : null}
      {citacao && p.comment && (
        <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink-3)', marginTop: 10 }}><MentionText text={p.comment} /></div>
      )}

      {/* ações */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 13 }}>
        <button data-acao="curtir" aria-label={curtiu ? 'Descurtir' : 'Curtir'} aria-pressed={curtiu} onClick={() => onCurtir(p)} style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', color: curtiu ? 'var(--ember)' : 'var(--ink-4)', cursor: 'pointer', padding: 0, fontSize: 12.5, fontFamily: MONO }}>
          <Coracao cheio={curtiu} />
          {curtidas > 0 ? curtidas : ''}
        </button>
        <button data-acao="comentar" aria-label={comentariosAbertos ? 'Fechar comentários' : 'Comentar'} onClick={() => onComentar(p)} style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', color: comentariosAbertos ? 'var(--ink-2)' : 'var(--ink-4)', cursor: 'pointer', padding: 0, fontSize: 12.5, fontFamily: MONO }}>
          <Ico d={icBalao} size={16} w={1.5} />
          {p.comments > 0 ? p.comments : ''}
        </button>
      </div>

      {/* A conversa abre AQUI, embaixo da publicação — não numa folha por cima.
          Comentário é resposta a um texto: tirar o texto da tela pra mostrar a
          resposta obriga a pessoa a lembrar do que se estava falando. */}
      {comentariosAbertos && blocoComentarios}
    </div>
  );
});

/* ── A folha de comentários ─────────────────────────────────────────────── */
// Dois níveis, e só. Responder a uma resposta não abre um terceiro degrau: o
// comentário fica no mesmo nível e nasce com `@fulano` no texto — que é como
// todo mundo já entende "esta é uma resposta a você".
// Quantos comentários aparecem de uma vez. Abrir a conversa inteira num post
// com 40 respostas empurra o resto do feed pra fora da tela e faz a pessoa
// rolar muito pra sair de um post só.
const COMENTARIOS_POR_LEVA = 3;

/** Concatena mantendo a ordem e descartando id repetido. */
function juntarSemRepetir(atual, novos) {
  const vistos = new Set((atual || []).map(p => p.id));
  const extras = (novos || []).filter(p => p && !vistos.has(p.id));
  return extras.length ? [...atual, ...extras] : atual;
}

function Comentarios({ session, clubId, post, meuId, podeModerar, onDelta, aoVivo }) {
  const [mostrando, setMostrando] = useState(COMENTARIOS_POR_LEVA);
  const [focado, setFocado] = useState(false);
  const [lista, setLista] = useState(null);
  const [texto, setTexto] = useState('');
  const [respondendo, setRespondendo] = useState(null); // { id, nome }
  const [enviando, setEnviando] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!post) { setLista(null); return; }
    let vivo = true;
    api.listComments(session, clubId, post.id)
      .then(r => { if (vivo) setLista(r.comments || []); })
      .catch(() => { if (vivo) setLista([]); });
    return () => { vivo = false; };
  }, [post, session, clubId]);

  // O servidor avisa; a folha recarrega. Recarregar a conversa inteira e mais
  // barato do que parece — sao poucos comentarios por publicacao — e nao corre
  // o risco de duplicar o que o proprio autor acabou de inserir na mao.
  useEffect(() => {
    if (!post || !aoVivo) return;
    const alvo = aoVivo;
    if (alvo.postId !== post.id) return;
    let vivo = true;
    api.listComments(session, clubId, post.id)
      .then(r => { if (vivo) setLista(r.comments || []); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [aoVivo, post, session, clubId]);

  const arvore = useMemo(() => {
    const raiz = [], filhos = {};
    (lista || []).forEach(c => {
      if (c.parentCommentId) (filhos[c.parentCommentId] ||= []).push(c);
      else raiz.push(c);
    });
    return raiz.map(c => ({ ...c, respostas: filhos[c.id] || [] }));
  }, [lista]);

  async function enviar() {
    const t = texto.trim();
    if (!t || enviando) return;
    setEnviando(true);
    try {
      const r = await api.createComment(session, clubId, post.id, t, respondendo?.id || null);
      setLista(prev => [...(prev || []), r.comment]);
      setTexto(''); setRespondendo(null);
      onDelta && onDelta(post.id, 1);
    } catch (e) {
      // silencioso: o texto continua no campo, dá pra tentar de novo
    } finally { setEnviando(false); }
  }

  function responder(c, alvo) {
    setRespondendo({ id: c.id, nome: alvo.author?.name });
    // A menção entra no texto — é ela que carrega "a quem" quando o degrau
    // some. `@nome ` no começo, sem duplicar se já estiver lá.
    const marca = '@' + String(alvo.author?.name || '').split(' ')[0] + ' ';
    setTexto(t => (t.startsWith(marca) ? t : marca + t));
    setTimeout(() => { try { inputRef.current && inputRef.current.focus(); } catch (_) {} }, 60);
  }

  async function apagar(c) {
    try {
      await api.deleteComment(session, clubId, post.id, c.id);
      setLista(prev => (prev || []).filter(x => x.id !== c.id && x.parentCommentId !== c.id));
      onDelta && onDelta(post.id, -1);
    } catch (_) {}
  }

  const Linha = ({ c, raiz, pai }) => (
    <div style={{ display: 'flex', gap: 9, marginTop: raiz ? 18 : 12, marginLeft: raiz ? 0 : 38 }}>
      <Avatar nome={c.author?.name} url={c.author?.avatarUrl} size={raiz ? 30 : 25} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>{c.author?.name}</span>
          <span style={{ fontFamily: MONO, fontSize: 9.5, color: 'var(--ink-4)' }}>{c.timeAgo}</span>
        </div>
        <div style={{ fontSize: 14.5, lineHeight: 1.5, color: 'var(--ink)', marginTop: 3 }}>
          <MentionText text={c.text} />
        </div>
        <div style={{ display: 'flex', gap: 13, marginTop: 4 }}>
          <button onClick={() => responder(pai || c, c)} style={{ border: 'none', background: 'transparent', color: 'var(--ink-4)', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer', padding: 0 }}>Responder</button>
          {(c.author?.id === meuId || podeModerar) && (
            <button onClick={() => apagar(c)} style={{ border: 'none', background: 'transparent', color: 'var(--ink-4)', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer', padding: 0 }}>Excluir</button>
          )}
        </div>
      </div>
    </div>
  );

  // Os mais RECENTES primeiro, e só COMENTARIOS_POR_LEVA de cada vez. O resto
  // fica atrás do "Ver mais" no fim, que revela de 3 em 3.
  // A pílula acompanha o texto: uma linha em repouso, cresce até 96px e volta
  // sozinha ao enviar. Sem isto o textarea fica travado em `rows={1}` e some
  // com o que a pessoa escreveu além da primeira linha.
  useEffect(() => {
    const el = inputRef.current;
    if (!el || !el.style) return;
    el.style.height = 'auto';
    el.style.height = Math.min(96, el.scrollHeight || 24) + 'px';
  }, [texto]);

  const maisNovosPrimeiro = [...arvore].reverse();
  const visiveis = maisNovosPrimeiro.slice(0, mostrando);
  const restantes = maisNovosPrimeiro.length - visiveis.length;

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
      {lista === null && (
        <div style={{ padding: '10px 0', color: 'var(--ink-4)', fontSize: 13 }}>Carregando…</div>
      )}

      {lista !== null && arvore.length === 0 && (
        <div style={{ padding: '4px 0 10px', color: 'var(--ink-4)', fontSize: 13.5 }}>
          Ninguém falou nada ainda. Seja a primeira voz.
        </div>
      )}

      {visiveis.map(c => (
        <React.Fragment key={c.id}>
          <Linha c={c} raiz />
          {c.respostas.map(r => <Linha key={r.id} c={r} pai={c} />)}
        </React.Fragment>
      ))}

      {restantes > 0 && (
        <button
          onClick={() => setMostrando(m => m + COMENTARIOS_POR_LEVA)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            border: 'none', background: 'transparent', cursor: 'pointer',
            padding: '8px 0 2px', color: 'var(--ink-4)',
          }}
        >
          <span style={{ height: 1, flex: 1, background: 'var(--line)' }} />
          <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            mais {restantes} {restantes === 1 ? 'comentário' : 'comentários'}
          </span>
          <span style={{ height: 1, flex: 1, background: 'var(--line)' }} />
        </button>
      )}

      {/* Composer — uma pílula de uma linha que cresce só quando precisa.
          Era um retângulo de 38px de altura mínima com borda em volta e um
          botão redondo sempre aceso do lado: três elementos disputando atenção
          embaixo de cada publicação. Aqui é um só, e ele fica quieto até
          alguém escrever. */}
      <div style={{ marginTop: 12 }}>
        {respondendo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8, paddingLeft: 2, fontSize: 12, color: 'var(--ink-4)' }}>
            <span style={{ width: 12, height: 1, background: 'var(--line-strong)', flex: 'none' }} />
            <span>respondendo <b style={{ color: 'var(--ink-2)', fontWeight: 600 }}>{respondendo.nome}</b></span>
            <button onClick={() => setRespondendo(null)} aria-label="Cancelar resposta" style={{ border: 'none', background: 'transparent', color: 'var(--ink-4)', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '0 2px' }}>×</button>
          </div>
        )}
        <div
          onFocusCapture={() => setFocado(true)}
          onBlurCapture={() => setFocado(false)}
          style={{
          display: 'flex', alignItems: 'flex-end', gap: 6,
          background: 'var(--paper)',
          border: `1px solid ${focado ? 'var(--line-strong)' : 'var(--line)'}`,
          borderRadius: 20, padding: '4px 4px 4px 14px',
          transition: 'border-color .18s ease',
        }}>
          <MentionInput
            inputRef={inputRef}
            value={texto}
            onChange={setTexto}
            placeholder="Comentar…"
            multiline
            rows={1}
            dropdownPosition="above"
            wrapStyle={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}
            inputStyle={{
              width: '100%', minHeight: 24, maxHeight: 96,
              border: 'none', background: 'transparent', outline: 'none', resize: 'none',
              color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit', lineHeight: 1.45,
              padding: '5px 0', display: 'block', overflowY: 'auto',
            }}
          />
          <button
            onClick={enviar}
            disabled={!texto.trim() || enviando}
            aria-label="Enviar comentário"
            style={{
              flex: 'none', width: 30, height: 30, borderRadius: '50%', border: 'none',
              // Fica invisível até haver texto: sem isso, um botão de ação
              // aceso embaixo de cada publicação do feed vira ruído.
              background: texto.trim() ? 'var(--ember)' : 'transparent',
              color: texto.trim() ? '#1c1208' : 'var(--ink-4)',
              opacity: enviando ? 0.5 : 1,
              cursor: texto.trim() ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background .18s ease, color .18s ease',
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 12h15" /><path d="M13 6l6 6-6 6" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── A tela ─────────────────────────────────────────────────────────────── */

export const EstudoCaderno = ({
  session, clubId, clubName, clubBook, yourPage = 0, viewerId,
  canPost, viewerIsModerator, onBack, onOpenProfile, headerExtra,
  // Denunciar publicação de outra pessoa (UGC — Apple 1.2) e abrir a gestão da
  // caixa de perguntas. O Estudo antigo tinha os dois; o Caderno tinha perdido.
  onReportPost = null as any, onOpenQboxAdmin = null as any,
  // Bandeja de stories do clube. Entra logo abaixo da capa, antes do progresso:
  // é o que há de mais recente e perecível na tela — story vence, post não.
  // `as any` porque o default `null` sozinho faz o TS inferir que a prop só
  // aceita null — as outras props deste componente não têm default e caem em
  // any implícito.
  storiesTray = null as any,
  // false enquanto o clube ainda está sendo buscado. Sem esperar, a tela carrega
  // uma lista SEM o livro e outra COM assim que ele chega.
  clubePronto = true,
  // Veio do "Comece o estudo — poste a 1ª reflexão": abre a escrita ao chegar
  // (pente fino, MOB-09).
  abrirEscrita = false,
}) => {
  const [posts, setPosts] = useState(null);
  const [avulsos, setAvulsos] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [fim, setFim] = useState(false);
  const [carregandoMais, setCarregandoMais] = useState(false);
  // Abre em RECENTES. A ordem do livro é a leitura "certa", mas quem volta ao
  // clube quer ver o que apareceu desde ontem — e na espinha o novo pode cair
  // em qualquer altura da página, inclusive fora da tela.
  const [ordem, setOrdem] = useState('recentes');
  const [filtro, setFiltro] = useState('all');
  const [busca, setBusca] = useState('');
  const [buscaAberta, setBuscaAberta] = useState(false);
  const [erro, setErro] = useState(null);
  const [recarga, setRecarga] = useState(0);   // muda -> refaz a leitura do zero

  // Quais publicações estão com a conversa aberta. Antes era UM post por vez,
  // porque a conversa vivia numa folha por cima da tela. Agora ela mora dentro
  // do card, então várias podem estar abertas ao mesmo tempo.
  const [comentariosAbertos, setComentariosAbertos] = useState(() => new Set());
  const alternarComentarios = useCallback((post) => {
    setComentariosAbertos(prev => {
      const n = new Set(prev);
      if (n.has(post.id)) n.delete(post.id); else n.add(post.id);
      return n;
    });
  }, []);
  const aplicaDelta = useCallback((postId, d) => {
    const aplica = (l) => (l || []).map(x => x.id === postId ? { ...x, comments: Math.max(0, (x.comments || 0) + d) } : x);
    setPosts(aplica); setAvulsos(aplica);
  }, []);
  const [menuDe, setMenuDe] = useState(null);
  const [avisoAoVivo, setAvisoAoVivo] = useState(null);   // { postId, commentId } — repassado a folha
  const [novasPublicacoes, setNovasPublicacoes] = useState(0);

  // composer
  const [composerAberto, setComposerAberto] = useState(false);
  // O voltar do Android fecha o que está por cima (composer, menu, busca) em
  // vez de sair do Estudo — e de levar junto o texto que estava sendo escrito.
  // Fecha pelo mesmo caminho do toque fora (para a gravação, solta a foto).
  const fecharComposerRef = useRef(null);
  useEffect(() => (composerAberto ? aoVoltarFechar(() => fecharComposerRef.current && fecharComposerRef.current()) : undefined), [composerAberto]);
  useEffect(() => (menuDe ? aoVoltarFechar(() => setMenuDe(null)) : undefined), [menuDe]);
  useEffect(() => (buscaAberta ? aoVoltarFechar(() => { setBuscaAberta(false); setBusca(''); }) : undefined), [buscaAberta]);
  const [modo, setModo] = useState('texto');
  const [tipo, setTipo] = useState('reflection');
  const [rascunho, setRascunho] = useState('');
  const [paginaAlvo, setPaginaAlvo] = useState(yourPage || 1);
  const [temPagina, setTemPagina] = useState(true);
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [cameraAberta, setCameraAberta] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erroComposer, setErroComposer] = useState(null);
  const voz = useVoiceRecorder();

  const totalPaginas = clubBook?.totalPages || clubBook?.total_pages || 0;
  const capaHue = clubBook?.coverHue ?? clubBook?.cover_hue ?? 30;
  const pctLido = totalPaginas ? Math.min(100, Math.round((yourPage / totalPaginas) * 100)) : 0;

  // ── carga ───────────────────────────────────────────────────────────────
  // Os filtros de mídia são locais (o servidor não indexa o jsonb), os outros
  // vão como filtro de verdade. `saved` só existe no servidor.
  const filtroServidor = filtro === 'questions' || filtro === 'saved' ? filtro : undefined;
  // O ID, não o objeto: `clubBook` chega novo a cada atualização silenciosa do
  // clube, e depender dele reiniciava a lista sem nada ter mudado.
  const livroId = clubBook?.id || null;

  const carregar = useCallback(async (mais = false) => {
    // Sem cursor NÃO existe "próxima página".
    //
    // A guarda antiga só olhava `fim`. Com o cursor nulo a chamada saía SEM
    // cursor — o servidor devolveria a primeira página de novo e a linha de
    // baixo concatenaria, duplicando a lista inteira.
    //
    // NÃO está provado que foi por aqui que a publicação apareceu duplicada
    // pro Wilmer em 02/09/2026: a janela (fim=false E cursor=null) é estreita,
    // e a reprodução em navegador falhou nas duas versões, com e sem esta
    // guarda. É um buraco real fechado, não um diagnóstico confirmado. O que
    // garante que a tela não repete é o juntarSemRepetir abaixo.
    if (mais && (fim || carregandoMais || !cursor)) return;
    if (mais) setCarregandoMais(true);
    try {
      setErro(null);
      const espinha = ordem === 'espinha' && filtro !== 'saved';
      const r = await api.listPosts(session, clubId, {
        limit: PAGINA,
        bookId: livroId,
        filter: filtroServidor,
        ordem: espinha ? 'espinha' : 'recentes',
        ...(mais && cursor ? (espinha ? { cursorPage: cursor.page, cursorAt: cursor.at } : { before: cursor.at }) : {}),
      });
      // Concatena SEM repetir id. ESTA é a garantia que vale: qualquer caminho
      // que devolva um post já presente — cursor errado, resposta repetida,
      // corrida entre duas cargas — para aqui, e a tela não mostra em dobro.
      setPosts(prev => (mais && prev ? juntarSemRepetir(prev, r.posts) : (r.posts || [])));
      setCursor(r.proximo || null);
      setFim(!!r.fim);
      // Os avulsos só entram na primeira leva, e só na espinha: fora dela a
      // ordenação já é cronológica e eles chegam misturados por direito.
      if (!mais && espinha) {
        try {
          const a = await api.listPostsAvulsos(session, clubId, { limit: 20, bookId: livroId });
          setAvulsos(a.posts || []);
        } catch (_) { setAvulsos([]); }
      } else if (!mais) setAvulsos([]);
    } catch (e) {
      setErro(e?.message || 'Não consegui carregar o Estudo.');
      if (!mais) setPosts([]);
    } finally { setCarregandoMais(false); }
  }, [session, clubId, livroId, ordem, filtro, filtroServidor, cursor, fim, carregandoMais]);

  // Recarrega do zero quando muda ordem/filtro. `carregar` não entra na lista de
  // dependências de propósito — ela muda a cada cursor novo e isso reiniciaria
  // a lista no meio da rolagem.
  useEffect(() => {
    // `clubBook` é um objeto NOVO a cada atualização silenciosa do clube, mesmo
    // sem nada ter mudado. Depender dele fazia a lista reiniciar sozinha; a
    // dependência agora é o ID.
    if (!clubePronto) return;
    setPosts(null); setAvulsos([]); setCursor(null); setFim(false);
    let vivo = true;
    (async () => {
      try {
        const espinha = ordem === 'espinha' && filtro !== 'saved';
        const r = await api.listPosts(session, clubId, {
          limit: PAGINA, bookId: livroId, filter: filtroServidor,
          ordem: espinha ? 'espinha' : 'recentes',
        });
        if (!vivo) return;
        setPosts(r.posts || []); setCursor(r.proximo || null); setFim(!!r.fim);
        if (espinha) {
          const a = await api.listPostsAvulsos(session, clubId, { limit: 20, bookId: livroId }).catch(() => ({ posts: [] }));
          if (vivo) setAvulsos(a.posts || []);
        }
      } catch (e) { if (vivo) { setErro(e?.message || 'Não consegui carregar.'); setPosts([]); } }
    })();
    return () => { vivo = false; };
  }, [session, clubId, livroId, clubePronto, ordem, filtro, filtroServidor, recarga]);

  // ── tempo real ──────────────────────────────────────────────────────────
  // Comentario e curtida chegam e mexem na tela na hora. Publicacao nova NAO
  // entra sozinha: quem esta lendo nao pode ter a lista empurrada sob o dedo —
  // vira um aviso que a pessoa toca quando quiser.
  useEffect(() => {
    if (!clubId) return;
    const fechar = api.openEstudoStream(session, clubId, (evento, d) => {
      if (evento === 'curtida') {
        if (d.por === viewerId) return;   // o meu proprio toque ja esta aplicado
        const aplica = (l) => (l || []).map(x => x.id === d.postId
          ? { ...x, reactions: { ...x.reactions, [d.kind]: d.count } } : x);
        setPosts(aplica); setAvulsos(aplica);
      } else if (evento === 'comentario') {
        setAvisoAoVivo({ postId: d.postId, commentId: d.commentId });
        const aplica = (l) => (l || []).map(x => x.id === d.postId
          ? { ...x, comments: (x.comments || 0) + 1 } : x);
        setPosts(aplica); setAvulsos(aplica);
      } else if (evento === 'publicacao') {
        if (d.por === viewerId) return;
        setNovasPublicacoes(n => n + 1);
      }
    });
    return fechar;
  }, [session, clubId, viewerId]);

  // rolagem infinita
  const sentinela = useRef(null);
  useEffect(() => {
    const el = sentinela.current;
    if (!el || fim) return;
    const io = new IntersectionObserver((es) => { if (es[0]?.isIntersecting) carregar(true); }, { rootMargin: '600px' });
    io.observe(el);
    return () => io.disconnect();
  }, [carregar, fim]);

  // ── a lista renderizada ─────────────────────────────────────────────────
  const visiveis = useMemo(() => {
    let base = [...(posts || [])];
    if (filtro === 'citacoes') base = base.filter(p => p.kind === 'citation');
    if (filtro === 'fotos') base = base.filter(p => !!p.photoUrl);
    if (filtro === 'audios') base = base.filter(p => !!p.audioUrl);
    const q = busca.trim().toLowerCase();
    if (q) base = base.filter(p => (p.text || '').toLowerCase().includes(q) || (p.author?.name || '').toLowerCase().includes(q));
    return base;
  }, [posts, filtro, busca]);

  // O marcador "VOCÊ LÊ AQUI" entra entre a última publicação antes da sua
  // página e a primeira depois dela. Só na espinha, e só se você já leu algo.
  const comMarcador = useMemo(() => {
    if (ordem !== 'espinha' || !yourPage) return visiveis;
    const idx = visiveis.findIndex(p => (p.page ?? 0) > yourPage);
    if (idx < 0) return [...visiveis, { id: '__aqui__', __marcador: true, page: yourPage }];
    return [...visiveis.slice(0, idx), { id: '__aqui__', __marcador: true, page: yourPage }, ...visiveis.slice(idx)];
  }, [visiveis, ordem, yourPage]);

  const rotuloPagina = useCallback((n) => (n === null || n === undefined) ? 'avulsa' : `p. ${n}`, []);

  // ── ações ───────────────────────────────────────────────────────────────
  const curtir = useCallback(async (p) => {
    const curtiu = (p.yourReactions || []).includes('tocou');
    const aplica = (lista) => (lista || []).map(x => x.id !== p.id ? x : {
      ...x,
      yourReactions: curtiu ? x.yourReactions.filter(k => k !== 'tocou') : [...(x.yourReactions || []), 'tocou'],
      reactions: { ...x.reactions, tocou: Math.max(0, (x.reactions?.tocou || 0) + (curtiu ? -1 : 1)) },
    });
    // DESFAZER E O CONTRARIO, nao a mesma coisa de novo.
    //
    // O catch chamava `aplica` outra vez — e `aplica` fecha sobre `curtiu`,
    // calculado do post ORIGINAL. Reaplicar somava de novo: uma curtida que
    // falhava deixava a contagem +2 e 'tocou' duplicado na lista. O comentario
    // dizia "desfaz" e o codigo fazia o oposto.
    const desfaz = (lista) => (lista || []).map(x => x.id !== p.id ? x : {
      ...x,
      yourReactions: curtiu ? [...(x.yourReactions || []), 'tocou'] : (x.yourReactions || []).filter(k => k !== 'tocou'),
      reactions: { ...x.reactions, tocou: Math.max(0, (x.reactions?.tocou || 0) + (curtiu ? 1 : -1)) },
    });
    setPosts(aplica); setAvulsos(aplica);
    try { await api.reactToPost(session, clubId, p.id, 'tocou'); }
    catch (_) { setPosts(desfaz); setAvulsos(desfaz); }
  }, [session, clubId]);

  const salvar = useCallback(async (p) => {
    const aplica = (lista) => (lista || []).map(x => x.id !== p.id ? x : {
      ...x,
      yourReactions: (x.yourReactions || []).includes('salvar')
        ? x.yourReactions.filter(k => k !== 'salvar') : [...(x.yourReactions || []), 'salvar'],
    });
    setPosts(aplica); setAvulsos(aplica);
    setMenuDe(null);
    try { await api.reactToPost(session, clubId, p.id, 'salvar'); } catch (_) {}
  }, [session, clubId]);

  const excluir = useCallback(async (p) => {
    setMenuDe(null);
    // Pergunta antes. O ⋯ fica colado no nome do autor e um toque de raspão
    // apagava a publicação na hora — o Estudo antigo sempre confirmou.
    if (!window.confirm('Excluir esta publicação? Não dá para desfazer.')) return;
    // O SERVIDOR PRIMEIRO, A TELA DEPOIS.
    //
    // Antes o post sumia da tela na hora e a falha era engolida em silencio.
    // Ele continuava no banco, os outros membros continuavam vendo, e para o
    // proprio autor ele REAPARECIA na proxima carga — sem nada explicando.
    // A tela irma (estudo2/index2.tsx) ja fazia na ordem certa.
    try {
      await api.deletePost(session, clubId, p.id);
    } catch (e) {
      window.alert((e && e.message) || 'Nao consegui excluir. Tente de novo.');
      return;
    }
    setPosts(l => (l || []).filter(x => x.id !== p.id));
    setAvulsos(l => (l || []).filter(x => x.id !== p.id));
  }, [session, clubId]);

  // Para os cartões de quiz e caixa (StudyPost): eles já confirmam e já mostram
  // o erro, então aqui é só o servidor e a lista.
  const apagarDoCartao = useCallback(async (postId) => {
    await api.deletePost(session, clubId, postId);
    setPosts(l => (l || []).filter(x => x.id !== postId));
    setAvulsos(l => (l || []).filter(x => x.id !== postId));
  }, [session, clubId]);

  // Quiz e caixa de perguntas guardam o conteúdo fora de `text` (perguntas,
  // opções, prompt). A Publicacao do Caderno só sabe texto/foto/áudio: eles
  // apareciam como um cartão VAZIO, e o membro não tinha onde responder o quiz
  // nem mandar a pergunta. Esses tipos usam o cartão do Estudo, que já sabe.
  const cartaoEspecial = (p) => (p.kind === 'quiz' || p.kind === 'question-box') ? (
    <StudyPost
      post={p} clubId={clubId} session={session} canPost={canPost}
      viewerId={viewerId} viewerIsModerator={viewerIsModerator}
      onDeletePost={apagarDoCartao}
      onReportPost={onReportPost || undefined}
      onOpenQboxAdmin={onOpenQboxAdmin || undefined}
    />
  ) : null;

  // Foco só quando o composer ABRE, e só no modo texto. Nada de autoFocus: o
  // painel fica montado o tempo todo, e autoFocus dispararia junto com a
  // página — foi assim que o teclado passou a abrir sozinho no Estudo.
  // O atraso deixa a folha subir antes; focar no mesmo quadro faz o iOS abrir
  // o teclado com o painel no meio do caminho.
  const rascunhoRef = useRef(null);
  useEffect(() => {
    if (!composerAberto || modo !== 'texto') return;
    const t = setTimeout(() => { try { rascunhoRef.current && rascunhoRef.current.focus(); } catch (_) {} }, 280);
    return () => clearTimeout(t);
  }, [composerAberto, modo]);

  const escritaAberta = useRef(false);
  useEffect(() => {
    if (!abrirEscrita || escritaAberta.current || !canPost || !clubePronto || posts === null) return;
    escritaAberta.current = true;
    abrirComposer('texto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abrirEscrita, canPost, clubePronto, posts]);

  function abrirComposer(m) {
    voz.reset();
    if (fotoPreview) URL.revokeObjectURL(fotoPreview);
    setFotoFile(null); setFotoPreview(null);
    setModo(m); setTipo(m === 'foto' ? 'citation' : 'reflection');
    setRascunho(''); setErroComposer(null);
    // Sem pagina registrada, chutar "1" seria colocar a publicacao no comeco
    // do livro sem que ninguem tenha dito isso. Ancorar vira escolha.
    setPaginaAlvo(yourPage || 1); setTemPagina(yourPage > 0);
    setComposerAberto(true);
  }
  function fecharComposer() {
    setComposerAberto(false);
    voz.reset();
    if (fotoPreview) URL.revokeObjectURL(fotoPreview);
    setFotoFile(null); setFotoPreview(null);
  }
  fecharComposerRef.current = fecharComposer;

  async function publicar() {
    if (salvando) return;
    setSalvando(true); setErroComposer(null);
    try {
      const content = { text: rascunho.trim() };
      if (modo === 'voz') {
        if (!voz.blob) throw new Error('Grave um áudio primeiro.');
        const up = await api.uploadClubPostAudio(session, voz.blob, voz.nomeDoArquivo('post'));
        content.audioUrl = up.url; content.audioDur = voz.dur; content.wave = voz.wave || null;
      }
      if (modo === 'foto') {
        if (!fotoFile) throw new Error('Escolha ou tire uma foto.');
        const up = await api.uploadClubPostPhoto(session, fotoFile);
        content.photoUrl = up.url; content.photoKind = 'page';
      }
      if (!content.text && !content.audioUrl && !content.photoUrl) throw new Error('Escreva alguma coisa.');
      const r = await api.createPost(session, clubId, {
        kind: tipo, content,
        page: temPagina ? paginaAlvo : undefined,
      });
      // Entra na hora, no lugar certo da espinha.
      const novo = r.post;
      if (novo) {
        if (!temPagina) setAvulsos(l => [novo, ...(l || [])]);
        else setPosts(l => {
          const arr = [...(l || []), novo];
          return ordem === 'espinha' ? arr.sort((a, b) => (a.page ?? 0) - (b.page ?? 0) || String(a.createdAt).localeCompare(String(b.createdAt))) : [novo, ...(l || [])];
        });
      }
      fecharComposer();
    } catch (e) {
      setErroComposer(e?.message || 'Não consegui publicar.');
    } finally { setSalvando(false); }
  }

  const vazio = posts !== null && visiveis.length === 0 && avulsos.length === 0;

  // Publicação nova num Estudo VAZIO: não há leitura a interromper — mostra
  // já. Antes o aviso "1 estudo novo" ficava por cima de "A espinha deste
  // livro está em branco", que já não era verdade (pente fino, MOB-10).
  const totalmenteVazio = posts !== null && posts.length === 0 && avulsos.length === 0;
  useEffect(() => {
    if (!totalmenteVazio || novasPublicacoes === 0 || busca) return;
    setNovasPublicacoes(0); setPosts(null); setCursor(null); setFim(false); setRecarga(r => r + 1);
  }, [totalmenteVazio, novasPublicacoes, busca]);
  const tipoAtual = TIPOS.find(t => t.key === tipo) || TIPOS[0];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'var(--paper)', display: 'flex', flexDirection: 'column', maxWidth: 460, margin: '0 auto' }}>
      <style>{`
        @keyframes bsPulse { 0% { box-shadow: 0 0 0 0 rgba(232,122,59,.55) } 100% { box-shadow: 0 0 0 22px rgba(232,122,59,0) } }
        @keyframes bsSpin { to { transform: rotate(360deg) } }
      `}</style>

      <div className="om-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {/* HERO — a capa como clima */}
        <div style={{ position: 'relative', height: 224, flex: 'none', overflow: 'hidden' }}>
          {clubBook?.coverUrl
            ? <img src={clubBook.coverUrl} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(18px) saturate(1.1)', transform: 'scale(1.2)' }} />
            : <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(120% 90% at 30% 15%, oklch(0.55 0.11 ${capaHue}), oklch(0.24 0.05 ${capaHue}))` }} />}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 38%, rgba(10,7,5,0.6) 78%, var(--paper) 100%)' }} />
          <div style={{ position: 'absolute', top: 'calc(12px + env(safe-area-inset-top, 0px))', left: 16, right: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 3 }}>
            <button onClick={onBack} aria-label="Voltar" style={{ width: 38, height: 38, borderRadius: '50%', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(20,14,10,0.42)', backdropFilter: 'blur(8px)', color: '#F3ECDE', cursor: 'pointer' }}>
              <Ico d={icVoltar} size={20} w={1.8} />
            </button>
            {headerExtra}
          </div>
          <div style={{ position: 'absolute', left: 20, right: 20, bottom: 16, zIndex: 2, display: 'flex', alignItems: 'flex-end', gap: 14 }}>
            <div style={{ width: 60, height: 88, flex: 'none', borderRadius: 4, overflow: 'hidden', boxShadow: '0 10px 26px rgba(0,0,0,.5)', background: `linear-gradient(155deg, oklch(0.78 0.09 ${capaHue}), oklch(0.45 0.10 ${capaHue}))`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 6, textAlign: 'center' }}>
              <ImgCapa src={clubBook?.coverUrl || undefined} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                fallback={<div className="serif" style={{ fontSize: 12, lineHeight: 1.05, color: '#241405' }}>{clubBook?.title}</div>} />
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingBottom: 2 }}>
              <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.15em', color: 'rgba(243,236,222,0.7)', marginBottom: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(clubName || 'CLUBE').toUpperCase()}</div>
              <div className="serif" style={{ fontSize: 25, lineHeight: 0.99, color: '#F8F2E6', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{clubBook?.title || 'Estudo'}</div>
              <div style={{ fontSize: 12, color: 'rgba(243,236,222,0.72)', marginTop: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clubBook?.author}</div>
            </div>
          </div>
        </div>

        {storiesTray}

        {/* onde VOCÊ está */}
        {totalPaginas > 0 && yourPage > 0 ? (
          <>
            <div style={{ padding: '14px 20px 6px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, height: 3, borderRadius: 3, background: 'var(--line-strong)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pctLido}%`, background: 'var(--ember)', borderRadius: 3 }} />
              </div>
              <div style={{ fontFamily: MONO, fontSize: 10.5, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>p. {yourPage} de {totalPaginas}</div>
            </div>
            {/* Só explica a Trilha quando a pessoa ESTÁ nela. Em Recentes a
                frase falaria de uma ordenação que não está na tela. */}
            {ordem === 'espinha' && (
              <div style={{ padding: '0 20px', fontSize: 12.5, color: 'var(--ink-4)' }}>
                na Trilha, o que está depois da sua página fica adiante
              </div>
            )}
          </>
        ) : ordem === 'espinha' ? (
          <div style={{ padding: '16px 20px 0', fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink-4)' }}>
            a Trilha segue a ordem do livro, não a do relógio — registre onde você
            parou e ela passa a mostrar onde você está.
          </div>
        ) : null}

        {/* O aviso de publicacao nova — parado, esperando o toque. */}
        {novasPublicacoes > 0 && (
          <div style={{ padding: '10px 20px 0' }}>
            <button
              onClick={() => { setNovasPublicacoes(0); setOrdem(o => o); setPosts(null); setCursor(null); setFim(false); setRecarga(r => r + 1); }}
              style={{
                width: '100%', padding: '11px 16px', borderRadius: 999, cursor: 'pointer',
                border: '1px solid var(--ember)', background: 'var(--ember-soft)',
                color: 'var(--ember)', fontSize: 13.5, fontWeight: 600,
              }}
            >
              {novasPublicacoes === 1 ? '1 estudo novo' : `${novasPublicacoes} estudos novos`} — tocar para ver
            </button>
          </div>
        )}

        {/* STICKY: busca / filtros / ordem */}
        <div style={{ position: 'sticky', top: 0, zIndex: 20, background: 'color-mix(in srgb, var(--paper) 88%, transparent)', backdropFilter: 'blur(12px)', padding: '12px 0 11px', marginTop: 12, borderBottom: '1px solid var(--line)' }}>
          {buscaAberta ? (
            <div style={{ padding: '0 20px', display: 'flex', alignItems: 'center', gap: 9 }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 9, padding: '9px 13px', background: 'var(--paper-2)', border: '1px solid var(--line-strong)', borderRadius: 12 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth={1.7} strokeLinecap="round">{icLupa}</svg>
                <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar no Estudo…" autoFocus style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit' }} />
              </div>
              <button onClick={() => { setBuscaAberta(false); setBusca(''); }} style={{ border: 'none', background: 'transparent', color: 'var(--ink-3)', fontSize: 14, cursor: 'pointer' }}>Fechar</button>
            </div>
          ) : (
            <div className="om-scroll" style={{ display: 'flex', alignItems: 'center', gap: 8, overflowX: 'auto', padding: '0 20px' }}>
              <button onClick={() => setBuscaAberta(true)} aria-label="Buscar" style={{ flex: 'none', width: 36, height: 32, borderRadius: 10, border: '1px solid var(--line-strong)', background: 'transparent', color: 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <Ico d={icLupa} size={15} w={1.8} />
              </button>
              {FILTROS.map(f => {
                const a = filtro === f.key;
                return <button key={f.key} onClick={() => setFiltro(f.key)} style={{ flex: 'none', height: 32, padding: '0 14px', borderRadius: 10, border: a ? '1px solid transparent' : '1px solid var(--line-strong)', background: a ? 'color-mix(in srgb, var(--ink) 12%, transparent)' : 'transparent', color: a ? 'var(--ink)' : 'var(--ink-3)', fontSize: 13, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>{f.label}</button>;
              })}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '11px 20px 0' }}>
            <span style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.12em', color: 'var(--ink-4)' }}>VER POR</span>
            <button onClick={() => setOrdem('recentes')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: MONO, fontSize: 11, letterSpacing: '.05em', color: ordem === 'recentes' ? 'var(--ink)' : 'var(--ink-4)', padding: 0 }}>RECENTES</button>
            <button onClick={() => setOrdem('espinha')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: MONO, fontSize: 11, letterSpacing: '.05em', color: ordem === 'espinha' ? 'var(--ink)' : 'var(--ink-4)', padding: 0 }}>TRILHA</button>
          </div>
        </div>

        {/* FEED */}
        <div style={{ padding: '18px 18px 150px' }}>
          {posts === null && <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-4)', fontSize: 14 }}>Carregando…</div>}
          {erro && <div style={{ padding: 20, textAlign: 'center', color: 'var(--ember)', fontSize: 13 }}>{erro}</div>}

          {vazio && (
            <div style={{ padding: '40px 24px 30px', textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, margin: '0 auto 22px', borderRadius: '50%', border: '1.5px dashed var(--line-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-4)' }}>
                <Ico d={<><path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H11v16H5.5A1.5 1.5 0 0 1 4 18.5z" /><path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H13v16h5.5A1.5 1.5 0 0 0 20 18.5z" /></>} size={26} w={1.4} />
              </div>
              <div className="serif" style={{ fontSize: 25, lineHeight: 1.12, color: 'var(--ink)', maxWidth: 280, margin: '0 auto 12px' }}>
                {busca ? 'Nada com essa palavra.' : 'A espinha deste livro está em branco.'}
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink-3)', maxWidth: 266, margin: '0 auto 26px' }}>
                {busca ? 'Tente outro termo.' : 'A primeira marca é sempre a mais difícil de fazer — e a que abre a conversa pro grupo inteiro.'}
              </div>
            </div>
          )}

          {comMarcador.map(p => {
            const cor = p.__marcador ? 'var(--ember)' : (CORDOTIPO[p.kind] || 'var(--ink-3)');
            return (
              <div key={p.id} data-post={p.__marcador ? undefined : p.id} style={{
                display: 'flex', alignItems: 'stretch',
                // Sem virtualizar: o navegador pula o que está fora da tela
                // sozinho, e o `contain-intrinsic-size` segura a barra de rolagem
                // no lugar. É o que permite mil publicações no mesmo scroll.
                contentVisibility: 'auto', containIntrinsicSize: '0 200px',
              }}>
                <div style={{ position: 'relative', width: 50, flex: 'none' }}>
                  <div style={{ position: 'absolute', left: 23, top: 0, bottom: 0, width: 2, background: 'var(--line-strong)' }} />
                  <div style={{ position: 'absolute', left: 16, top: 5, width: 16, height: 16, borderRadius: '50%', background: 'var(--paper)', border: `2px solid ${cor}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: cor }} />
                  </div>
                  <div style={{ position: 'absolute', left: -4, right: 6, top: 26, textAlign: 'center', fontFamily: MONO, fontSize: 9, color: 'var(--ink-4)', lineHeight: 1.2 }}>{rotuloPagina(p.page ?? null)}</div>
                </div>
                <div style={{ flex: 1, minWidth: 0, paddingBottom: p.__marcador ? 0 : 22, position: 'relative' }}>
                  {p.__marcador ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0 9px' }}>
                      <div style={{ flex: 'none', fontFamily: MONO, fontSize: 10, letterSpacing: '0.14em', color: 'var(--ember)' }}>VOCÊ LÊ AQUI</div>
                      <div style={{ flex: 1, height: 1, background: 'repeating-linear-gradient(90deg,var(--ember) 0 6px,transparent 6px 12px)', opacity: 0.6 }} />
                    </div>
                  ) : cartaoEspecial(p) || (
                    <Publicacao
                      p={p}
                      rotulo={ROTULO[p.kind] || 'ESTUDO'}
                      meuId={viewerId}
                      podeGerenciar={viewerIsModerator || p.author?.id === viewerId}
                      onCurtir={curtir}
                      onComentar={alternarComentarios}
                      comentariosAbertos={comentariosAbertos.has(p.id)}
                      blocoComentarios={<Comentarios session={session} clubId={clubId} post={p} meuId={viewerId} podeModerar={viewerIsModerator} aoVivo={avisoAoVivo} onDelta={aplicaDelta} />}
                      onMenu={setMenuDe}
                      onPerfil={onOpenProfile}
                    />
                  )}
                </div>
              </div>
            );
          })}

          {/* sentinela da rolagem infinita */}
          {!fim && posts !== null && <div ref={sentinela} style={{ height: 1 }} />}
          {carregandoMais && <div style={{ padding: 22, textAlign: 'center', color: 'var(--ink-4)', fontFamily: MONO, fontSize: 11 }}>carregando…</div>}

          {/* os sem âncora */}
          {avulsos.length > 0 && (
            <div style={{ marginTop: 26 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.14em', color: 'var(--ink-4)' }}>SEM PÁGINA</div>
                <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
              </div>
              {avulsos.map(p => (
                <div key={p.id} data-post={p.id} style={{ paddingLeft: 50, contentVisibility: 'auto', containIntrinsicSize: '0 180px' }}>
                  {cartaoEspecial(p) || <Publicacao
                    p={p} rotulo={ROTULO[p.kind] || 'ESTUDO'} meuId={viewerId}
                    podeGerenciar={viewerIsModerator || p.author?.id === viewerId}
                    onCurtir={curtir} onComentar={alternarComentarios} onMenu={setMenuDe} onPerfil={onOpenProfile}
                    comentariosAbertos={comentariosAbertos.has(p.id)}
                    blocoComentarios={<Comentarios session={session} clubId={clubId} post={p} meuId={viewerId} podeModerar={viewerIsModerator} aoVivo={avisoAoVivo} onDelta={aplicaDelta} />}
                  />}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* BARRA DE CAPTURA */}
      {canPost && (
        <div style={{ flex: 'none', padding: '14px 18px calc(16px + env(safe-area-inset-bottom, 0px))', background: 'linear-gradient(transparent, var(--paper) 38%)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 8px 8px 18px', background: 'var(--paper-2)', border: '1px solid var(--line-strong)', borderRadius: 30, boxShadow: vazio ? '0 0 0 2px var(--ember), 0 12px 30px rgba(0,0,0,.3)' : '0 12px 30px rgba(0,0,0,.3)' }}>
            <button onClick={() => abrirComposer('texto')} style={{ flex: 1, textAlign: 'left', border: 'none', background: 'transparent', color: 'var(--ink-3)', fontSize: 15, fontFamily: 'inherit', cursor: 'pointer', padding: '8px 0' }}>Compartilhe com o grupo…</button>
            <button onClick={() => abrirComposer('voz')} aria-label="Gravar voz" style={{ width: 42, height: 42, flex: 'none', borderRadius: '50%', border: '1px solid var(--line-strong)', background: 'var(--paper)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Ico d={icMic} size={19} /></button>
            <button onClick={() => abrirComposer('foto')} aria-label="Foto" style={{ width: 42, height: 42, flex: 'none', borderRadius: '50%', border: '1px solid var(--line-strong)', background: 'var(--paper)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Ico d={icFoto} size={19} w={1.5} /></button>
            <button onClick={() => abrirComposer('texto')} aria-label="Publicar" style={{ width: 48, height: 48, flex: 'none', borderRadius: '50%', border: 'none', background: 'var(--ember)', color: '#1c1208', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 6px 16px rgba(232,122,59,.4)' }}><Ico d={icMais} size={22} w={2.2} /></button>
          </div>
        </div>
      )}

      {/* COMPOSER */}
      <div onClick={fecharComposer} style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(8,5,3,0.55)', backdropFilter: 'blur(2px)', transition: 'opacity .3s', opacity: composerAberto ? 1 : 0, pointerEvents: composerAberto ? 'auto' : 'none' }} />
      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 61, maxWidth: 460, margin: '0 auto', background: 'var(--paper-2)', borderRadius: '26px 26px 0 0', boxShadow: '0 -16px 50px rgba(0,0,0,.4)', transform: composerAberto ? 'translateY(0)' : 'translateY(105%)', transition: 'transform .42s cubic-bezier(.32,.72,.3,1)', maxHeight: '90%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '10px 0 4px', display: 'flex', justifyContent: 'center', flex: 'none' }}><div style={{ width: 38, height: 4, borderRadius: 4, background: 'var(--line-strong)' }} /></div>
        <div style={{ padding: '6px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 'none' }}>
          <div className="serif" style={{ fontSize: 22, color: 'var(--ink)' }}>Publicar</div>
          <button onClick={fecharComposer} aria-label="Fechar" style={{ width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'var(--paper-3)', color: 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Ico d={icFechar} size={16} w={1.8} /></button>
        </div>

        <div style={{ display: 'flex', gap: 7, padding: '14px 20px 0', flex: 'none' }}>
          {MODOS.map(m => { const a = modo === m.key; return (
            <button key={m.key} onClick={() => { voz.reset(); setModo(m.key); }} style={{ flex: 1, padding: '10px 0', borderRadius: 12, border: a ? '1px solid var(--ink)' : '1px solid var(--line-strong)', background: a ? 'var(--ink)' : 'transparent', color: a ? 'var(--paper)' : 'var(--ink-3)', fontSize: 13.5, fontWeight: 500, cursor: 'pointer' }}>{m.label}</button>
          ); })}
        </div>

        <div className="om-scroll" style={{ overflowY: 'auto', padding: '16px 20px 0', flex: 1, minHeight: 0 }}>
          {erroComposer && <div style={{ padding: '9px 12px', borderRadius: 10, marginBottom: 12, background: 'rgba(232,122,59,0.12)', color: 'var(--ember)', fontSize: 13 }}>{erroComposer}</div>}

          {modo !== 'voz' && (
            <div className="om-scroll" style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingBottom: 14 }}>
              {TIPOS.map(t => { const a = tipo === t.key; return (
                <button key={t.key} onClick={() => setTipo(t.key)} style={{ flex: 'none', padding: '7px 14px', borderRadius: 20, border: `1px solid ${a ? t.cor : 'var(--line-strong)'}`, background: a ? `color-mix(in srgb, ${t.cor} 18%, transparent)` : 'transparent', color: a ? t.cor : 'var(--ink-3)', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>{t.label}</button>
              ); })}
            </div>
          )}

          {modo === 'texto' && (
            <MentionInput
              inputRef={rascunhoRef}
              value={rascunho} onChange={setRascunho} multiline
              placeholder={tipoAtual.place}
              inputStyle={{ width: '100%', minHeight: 128, border: 'none', background: 'transparent', outline: 'none', resize: 'none', color: 'var(--ink)', fontFamily: SERIF, fontSize: 21, lineHeight: 1.4 }}
            />
          )}

          {modo === 'voz' && (
            voz.stage !== 'recorded' ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '14px 0 8px', minHeight: 170, justifyContent: 'center' }}>
                <button onClick={() => voz.stage === 'recording' ? voz.stop() : voz.start()} style={{ width: 84, height: 84, borderRadius: '50%', border: 'none', background: 'var(--ember)', color: '#1c1208', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', animation: voz.stage === 'recording' ? 'bsPulse 1.5s ease-out infinite' : 'none' }}>
                  {voz.stage === 'recording'
                    ? <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2.5" /></svg>
                    : <Ico d={icMic} size={32} />}
                </button>
                {voz.stage === 'recording' && <div style={{ fontFamily: MONO, fontSize: 25, color: 'var(--ink)', letterSpacing: '0.06em', margin: '18px 0 10px' }}>{fmtTime(voz.sec)}</div>}
                <div style={{ fontSize: 13.5, color: 'var(--ink-3)', marginTop: 12, textAlign: 'center' }}>
                  {voz.err || (voz.stage === 'recording' ? 'Gravando… toque para parar' : 'Toque para gravar e falar com o grupo')}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.14em', color: 'var(--ember)', marginBottom: 10 }}>SUA NOTA DE VOZ</div>
                {voz.url && <AudioPlayer url={voz.url} dur={voz.dur} wave={voz.wave} />}
                <button onClick={voz.regravar} style={{ alignSelf: 'flex-start', marginTop: 12, border: 'none', background: 'transparent', color: 'var(--ink-3)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}>Regravar</button>
                <MentionInput value={rascunho} onChange={setRascunho} multiline placeholder="Sobre o que é esse áudio? — para quem preferir ler"
                  inputStyle={{ width: '100%', minHeight: 74, marginTop: 14, border: '1px solid var(--line)', background: 'var(--paper)', borderRadius: 12, padding: '12px 14px', outline: 'none', resize: 'none', color: 'var(--ink)', fontFamily: SERIF, fontSize: 18, lineHeight: 1.4 }} />
              </div>
            )
          )}

          {modo === 'foto' && (
            !fotoPreview ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div onClick={() => setCameraAberta(true)} style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', background: '#1a120b', height: 260, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <div style={{ position: 'absolute', inset: 14, border: '2px dashed rgba(255,255,255,.28)', borderRadius: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: 'rgba(255,255,255,.85)' }}>
                    <Ico d={icFoto} size={38} w={1.4} />
                    <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.08em', textAlign: 'center', padding: '0 20px' }}>FOTOGRAFE O TRECHO — A FOTO É A CITAÇÃO</div>
                  </div>
                </div>
                <button onClick={() => setCameraAberta(true)} style={{ marginTop: 18, padding: '12px 24px', borderRadius: 14, border: 'none', background: 'var(--ember)', color: '#1c1208', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Abrir câmera</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ background: '#f4efe4', padding: '9px 9px 14px', borderRadius: 3, boxShadow: '0 16px 36px rgba(0,0,0,.4)', transform: 'rotate(-2deg)', width: 212 }}>
                  <img src={fotoPreview} alt="" style={{ width: '100%', borderRadius: 1, display: 'block' }} />
                </div>
                <MentionInput value={rascunho} onChange={setRascunho} multiline placeholder="Uma legenda curta: o que esse trecho mexeu em você?"
                  inputStyle={{ width: '100%', minHeight: 74, marginTop: 16, border: '1px solid var(--line)', background: 'var(--paper)', borderRadius: 12, padding: '12px 14px', outline: 'none', resize: 'none', color: 'var(--ink)', fontFamily: SERIF, fontStyle: 'italic', fontSize: 17, lineHeight: 1.4 }} />
              </div>
            )
          )}

          {/* âncora de página — o que dá lugar à publicação na espinha */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0 4px', padding: '12px 14px', background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 14 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.13em', color: 'var(--ink-4)' }}>ONDE NO LIVRO</div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 3 }}>
                {temPagina ? 'é isto que coloca você na espinha' : 'sem página, vai pro fim da lista'}
              </div>
            </div>
            {temPagina ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
                <button onClick={() => setPaginaAlvo(v => Math.max(1, v - 1))} style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid var(--line-strong)', background: 'transparent', color: 'var(--ink)', fontSize: 17, cursor: 'pointer' }}>−</button>
                <input value={paginaAlvo} onChange={e => setPaginaAlvo(Math.max(1, parseInt(e.target.value.replace(/\D/g, ''), 10) || 1))} inputMode="numeric" style={{ width: 54, textAlign: 'center', border: 'none', background: 'transparent', color: 'var(--ink)', fontFamily: SERIF, fontSize: 24, outline: 'none' }} />
                <button onClick={() => setPaginaAlvo(v => v + 1)} style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid var(--line-strong)', background: 'transparent', color: 'var(--ink)', fontSize: 17, cursor: 'pointer' }}>+</button>
              </div>
            ) : (
              <button onClick={() => setTemPagina(true)} style={{ flex: 'none', padding: '8px 14px', borderRadius: 10, border: '1px solid var(--line-strong)', background: 'transparent', color: 'var(--ink-3)', fontSize: 13, cursor: 'pointer' }}>Ancorar</button>
            )}
          </div>
          {temPagina && (
            <button onClick={() => setTemPagina(false)} style={{ border: 'none', background: 'transparent', color: 'var(--ink-4)', fontSize: 12.5, cursor: 'pointer', padding: '4px 0 0' }}>não é sobre um trecho específico</button>
          )}
        </div>

        <div style={{ flex: 'none', padding: '14px 20px calc(16px + env(safe-area-inset-bottom,0px))', display: 'flex', gap: 10 }}>
          <button onClick={publicar} disabled={salvando} style={{ flex: 1, padding: '15px 20px', borderRadius: 999, border: 'none', background: 'var(--ember)', color: '#1c1208', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
            {salvando ? 'publicando…' : 'Publicar no Estudo'}
          </button>
        </div>
      </div>

      {cameraAberta && (
        <CameraCapture
          onCapture={(f) => { if (fotoPreview) URL.revokeObjectURL(fotoPreview); setFotoFile(f); setFotoPreview(URL.createObjectURL(f)); setCameraAberta(false); }}
          onClose={() => setCameraAberta(false)}
        />
      )}

      {/* menu do ⋯ */}
      {menuDe && (
        <>
          <div onClick={() => setMenuDe(null)} style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(8,5,3,0.5)' }} />
          <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 81, maxWidth: 460, margin: '0 auto', background: 'var(--paper-2)', borderRadius: '22px 22px 0 0', padding: '10px 16px calc(16px + env(safe-area-inset-bottom,0px))' }}>
            <div style={{ padding: '4px 0 10px', display: 'flex', justifyContent: 'center' }}><div style={{ width: 38, height: 4, borderRadius: 4, background: 'var(--line-strong)' }} /></div>
            <button onClick={() => salvar(menuDe)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 8px', border: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 15, cursor: 'pointer', textAlign: 'left' }}>
              <Ico d={icMarcar} size={17} />{(menuDe.yourReactions || []).includes('salvar') ? 'Tirar dos salvos' : 'Salvar'}
            </button>
            {(viewerIsModerator || menuDe.author?.id === viewerId) && (
              <button onClick={() => excluir(menuDe)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 8px', border: 'none', background: 'transparent', color: '#E2574C', fontSize: 15, cursor: 'pointer', textAlign: 'left' }}>
                <Ico d={<><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></>} size={17} />Excluir
              </button>
            )}
            {onReportPost && menuDe.author?.id !== viewerId && (
              <button onClick={() => { const alvo = menuDe; setMenuDe(null); onReportPost(alvo); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 8px', border: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 15, cursor: 'pointer', textAlign: 'left' }}>
                <Ico d={<><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" /></>} size={17} />Denunciar
              </button>
            )}
            <button onClick={() => setMenuDe(null)} style={{ width: '100%', padding: '14px 8px', marginTop: 4, border: 'none', background: 'transparent', color: 'var(--ink-3)', fontSize: 15, cursor: 'pointer' }}>Cancelar</button>
          </div>
        </>
      )}

    </div>
  );
};

export default EstudoCaderno;
